import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { getCsrfToken } from '../utils/csrf';

// Environment-aware logging - only logs in development
const isDevelopment = process.env.NODE_ENV !== 'production';
const devLog = (...args: unknown[]): void => {
  if (isDevelopment) {
    void args;
  }
};
const devWarn = (...args: unknown[]): void => {
  if (isDevelopment) {
    void args;
  }
};
const devError = (...args: unknown[]): void => {
  if (isDevelopment) {
    console.error(...args);
  }
};

// Session expiration event system - allows React context to react to auth changes
// without coupling authService to React internals
type SessionExpiredCallback = () => void;
let onSessionExpiredCallback: SessionExpiredCallback | null = null;

export const onSessionExpired = (callback: SessionExpiredCallback): (() => void) => {
  onSessionExpiredCallback = callback;
  return () => { onSessionExpiredCallback = null; };
};

export const notifySessionExpired = (): void => {
  if (onSessionExpiredCallback) {
    onSessionExpiredCallback();
  }
};

// 🔒 SECURITY: Token fields removed - tokens are ONLY in httpOnly cookies
interface RefreshResponse {
  success: boolean;
  // No token fields - tokens in httpOnly cookies
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface LoginResponse {
  success: boolean;
  user: {
    id: number;
    email: string;
    name: string;
    role: string;
    client_id: number | null;
  };
  // No token fields - tokens in httpOnly cookies
}

interface UserRoleResponse {
  data: {
    role: string;
    [key: string]: any;
  };
}

const apiClient: AxiosInstance = axios.create({
  withCredentials: true // 🔒 SECURITY: Include httpOnly cookies in all requests
});

export type RefreshOutcome = 'refreshed' | 'expired' | 'unavailable';
let refreshFlight: Promise<RefreshOutcome> | null = null;

/** One cookie renewal for dashboard and MCP. A caller's cancellation must not cancel other waiters. */
export const refreshSession = (): Promise<RefreshOutcome> => {
  if (refreshFlight) return refreshFlight;
  if (typeof window === 'undefined') return Promise.resolve('unavailable');
  refreshFlight = axios.post<RefreshResponse>('/api/auth/refresh', {}, {
    withCredentials: true,
    headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-store' },
    timeout: 10000,
  }).then(response => response.data?.success === true ? 'refreshed' as const : 'unavailable' as const)
    .catch((error: unknown): RefreshOutcome => {
      // Only an explicit auth rejection is a definitive end to the session.
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      return status === 401 || status === 403 ? 'expired' : 'unavailable';
    }).finally(() => { refreshFlight = null; });
  return refreshFlight;
};

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 🔒 SECURITY: Tokens now in httpOnly cookies, sent automatically via withCredentials
    // No need to manually set Authorization header from localStorage

    // 🔒 SECURITY: Add CSRF token from cookie to header for state-changing requests
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method?.toUpperCase() || '')) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['x-xsrf-token'] = csrfToken;
      }
    }

    // 🔑 CLIENT CONTEXT: Send selected client context for admin_globale
    // admin_globale has client_id: null in JWT. The backend authMiddleware reads
    // this header to resolve req.user.client_id for all downstream controllers.
    if (typeof window !== 'undefined' && window.localStorage) {
      const selectedClientId = localStorage.getItem('adminSelectedClientId');
      // Keep the tenant captured on the first attempt, including 401 retries.
      // The global picker may have changed while authentication was refreshing.
      if (selectedClientId && !config.headers.has('x-client-context')) {
        config.headers.set('x-client-context', selectedClientId);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 429 Too Many Requests - do NOT retry, just log and reject
    if (error.response?.status === 429) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      const outcome = await refreshSession();
      if (outcome === 'refreshed') return apiClient(originalRequest);
      if (outcome === 'expired') notifySessionExpired();
    }

    return Promise.reject(error);
  }
);

const authService = {
  isAuthenticated: (): boolean => {
    // 🔒 SECURITY: Cannot check httpOnly cookie from JavaScript
    // Authentication will be verified server-side on each request
    // This is a client-side hint only - actual auth is done by backend
    if (typeof window === 'undefined') return false;
    
    // Optimistic check: assume authenticated until server says otherwise
    // The 401 interceptor will handle auth failures
    return true;
  },

  checkUserRole: async (): Promise<string | null> => {
    try {
      const response = await apiClient.get<UserRoleResponse>('/api/users/me');
      devLog('Risposta dal server:', response.data);

      if (response.data && response.data.data && response.data.data.role) {
        return response.data.data.role;
      } else {
        devError('Struttura dati non valida nella risposta:', response.data);
        return null;
      }
    } catch (error) {
      devError('Errore nel controllo del ruolo:', error);
      throw error;
    }
  },

  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    // 🔒 SECURITY: Backend sets httpOnly cookie automatically
    const response = await axios.post<LoginResponse>('/api/auth/login', credentials, {
      withCredentials: true
    });
    
    // 🔒 SECURITY: No longer storing tokens in localStorage
    // Token is in httpOnly cookie, safe from XSS attacks
    
    return response.data;
  },

  logout: async (): Promise<void> => {
    devLog('🚪 Logout in corso...');

    try {
      // 🔒 SECURITY: Call server to clear httpOnly cookies
      await axios.post('/api/auth/logout', {}, {
        withCredentials: true
      });
    } catch (error) {
      devError('Errore durante logout:', error);
      // Continue with local cleanup even if server call fails
    }
    
    // Clear non-sensitive localStorage data
    if (typeof window !== 'undefined') {
      if (window.localStorage) {
        // 🔒 SECURITY: Keep token cleanup for backward compatibility
        // but tokens are now in httpOnly cookies
        localStorage.removeItem('token');
        localStorage.removeItem("handoverToken");
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userId');
        localStorage.removeItem('adminSelectedClientId');
      }
      if (window.sessionStorage) {
        sessionStorage.clear();
      }
    }
    
    if (typeof window !== 'undefined') {
      setTimeout(() => {
        window.location.href = '/#/login';
      }, 100);
    }
  }
};

export const refreshToken = async (): Promise<string | null> => {
  if (typeof window === 'undefined') devWarn('⚠️ SSR context, cannot refresh token');
  return (await refreshSession()) === 'refreshed' ? 'token-refreshed' : null;
};

export { authService, apiClient };

// Re-export common axios types and utilities for centralized imports
// This allows other files to import from authService instead of directly from 'axios'
export { AxiosError, isAxiosError } from 'axios';
export type { AxiosResponse, AxiosInstance } from 'axios';
